/**
 * Workspace stage-restore logic (ProjectWorkspace.jsx), extracted to a dependency-free module
 * so it is unit-testable without mounting the full page (5,000+ line component, heavy
 * MSAL/AppContext/antd/child-component surface — see
 * .claude/lessons/workspace-active-step-persist-desync.md for the cost of testing that surface
 * directly, and the "Follow-up" note on commit b222dff, which this extraction fulfils).
 *
 * These three functions were already pure — no closures over component state, no hooks — only
 * their location (inline in ProjectWorkspace.jsx, downstream of antd/MSAL/8 heavy child-component
 * imports at module scope) made them untestable without a full render harness. Moving them here
 * is a mechanical, behavior-preserving extraction: no logic changed.
 *
 * Commandment V (Workflow Order is Law): this is the client-side mirror of stage-gating used to
 * decide which already-server-unlocked stage to *restore* the UI to after a reload. It does not
 * grant access — `getStepNavigationGate` denies any stage whose prerequisites are not satisfied,
 * and the API layer remains the authoritative gate for every stage-scoped action.
 */

import { isOnboardingWorkflowGateSatisfied } from "./projectOnboardingWizard";

/**
 * Linear workflow: each step unlocks only after the previous phase is finalized.
 */
export function getStepNavigationGate(stepId, project, userRole) {
  if (!project || stepId === 1) return { allowed: true, hint: "" };
  if (userRole === "COMPANY_DELEGATE" && stepId === 4 && project?.profile?.gapAnalysisWizard?.delegationFinalized) {
    return { allowed: true, hint: "" };
  }
  const prof = project.profilingStatus || "Draft";
  const scope = project.scopingStatus || "Draft";
  const onboardingGateOk = isOnboardingWorkflowGateSatisfied(project?.profile || {});

  if (stepId === 2) {
    if (prof === "Finalized") return { allowed: true, hint: "" };
    return {
      allowed: false,
      hint: "Scoping opens after Profiling is finished - finalize the company profile first.",
    };
  }
  if (stepId === 3) {
    if (scope === "Finalized") return { allowed: true, hint: "" };
    return {
      allowed: false,
      hint: "Project Onboarding opens after Scoping is finalized.",
    };
  }
  if (stepId === 4) {
    if (onboardingGateOk) return { allowed: true, hint: "" };
    return {
      allowed: false,
      hint: "Gap Analysis opens after Project Onboarding is complete (contract published).",
    };
  }
  if (stepId === 5) {
    if (!onboardingGateOk) {
      return {
        allowed: false,
        hint: "This phase opens after Project Onboarding is complete.",
      };
    }
    const gw = project?.profile?.gapAnalysisWizard || {};
    const gapStatus = gw.gapAnalysisStatus || "Draft";
    const pushed = gw.pushedToDashboard === true;
    if (pushed || gapStatus === "Finalized") return { allowed: true, hint: "" };
    return {
      allowed: false,
      hint: "This phase opens after you push gap data to the dashboard or the Gap Analysis is finalized.",
    };
  }
  if (stepId >= 6) {
    if (!onboardingGateOk) {
      return {
        allowed: false,
        hint: "This phase opens after Project Onboarding is complete.",
      };
    }
    const gw = project?.profile?.gapAnalysisWizard || {};
    const gapStatus = gw.gapAnalysisStatus || "Draft";
    const pushed = gw.pushedToDashboard === true;
    if (!(pushed || gapStatus === "Finalized")) {
      return {
        allowed: false,
        hint: "This phase opens after you push gap data to the dashboard or the Gap Analysis is finalized.",
      };
    }
    const crw = project?.profile?.complianceRegisterPackage?.workflow;
    if (!(crw === "sent_to_spoc" || crw === "spoc_acknowledged")) {
      return {
        allowed: false,
        hint: "Risk Analysis opens after the compliance register has been sent to the company SPOC (Organizational Setup).",
      };
    }
    const ow = project?.profile?.organizationalSetupWizard;
    const hasOrgWizard = ow != null && typeof ow === "object";
    // Avoid flicker to step 5 when profile refetch briefly omits wizard JSON: allow Risk Analysis if the user
    // is already on step 6+ or any risk-register payload exists (real reset clears both wizard and risk work).
    if (!hasOrgWizard) {
      const savedAtLeastSix =
        typeof project?.profile?.workspaceActiveStep === "number" &&
        project?.profile?.workspaceActiveStep >= 6;
      const rr = project?.profile?.riskRegisterPackage;
      const hasRiskWorkbench =
        rr != null &&
        typeof rr === "object" &&
        (Array.isArray(rr.rows) ? rr.rows.length > 0 : Object.keys(rr).length > 0);
      if (!savedAtLeastSix && !hasRiskWorkbench) {
        return {
          allowed: false,
          hint: "Organizational Setup data is missing (for example after a reset). Complete step 5 before Risk Analysis.",
        };
      }
    }
    if (stepId === 6) return { allowed: true, hint: "" };
    // Step 7+ requires the SOA phase to be reached (AI narrative pass complete).
    const soaPkg = project?.profile?.statementOfApplicabilityPackage;
    const soaPhaseComplete = !!(soaPkg && typeof soaPkg === "object" && soaPkg.lastAiDetailsAt);
    if (!soaPhaseComplete) {
      return {
        allowed: false,
        hint: "Control Implementation opens after the Statement of Applicability narrative is generated.",
      };
    }
    return { allowed: true, hint: "" };
  }
  return { allowed: true, hint: "" };
}

/** Prefer a step only if allowed; otherwise walk down to the latest unlocked step */
export function clampStepToAllowed(preferredStep, project, userRole) {
  if (!project) return 1;
  const n = Math.min(9, Math.max(1, preferredStep | 0));
  for (let s = n; s >= 1; s--) {
    if (getStepNavigationGate(s, project, userRole).allowed) return s;
  }
  return 1;
}

/**
 * Decides which of the 11 workflow stages to restore a page-load to. Priority order:
 * (1) COMPANY_DELEGATE with a finalized gap-analysis delegation always lands in the delegate
 *     lane (step 6 if Risk Analysis is unlocked and either already delegate-owned or previously
 *     saved there, else step 4);
 * (2) a valid persisted `project.profile.workspaceActiveStep` (1-9) wins unconditionally;
 * (3) the legacy `project.currentStep`;
 * (4) only if neither is set, `profilingStatus === "Finalized" ? 2 : 1` — this fallback can
 *     never resolve above step 2, which is why backlog #5 (a persist call missing at a
 *     forward-navigation handler) could strand a user's restore point at step 1.
 */
export function getWorkspaceStepForProject(project, user) {
  if (!project) return 1;
  if (user?.role === "COMPANY_DELEGATE" && project?.profile?.gapAnalysisWizard?.delegationFinalized) {
    if (getStepNavigationGate(6, project, user?.role).allowed) {
      const rr = project?.profile?.riskRegisterPackage;
      const riskWithDelegate = rr && typeof rr === "object" && rr.workflow === "with_delegate";
      const saved = project?.profile?.workspaceActiveStep;
      if (riskWithDelegate || saved === 6) return 6;
    }
    return 4;
  }
  const ow = project?.profile?.organizationalSetupWizard;
  const hasOrgWizard = ow != null && typeof ow === "object";
  const savedStep = project?.profile?.workspaceActiveStep;
  const crwRestore = project?.profile?.complianceRegisterPackage?.workflow;
  const crSentRestore = crwRestore === "sent_to_spoc" || crwRestore === "spoc_acknowledged";
  if (typeof savedStep === "number" && !Number.isNaN(savedStep) && savedStep >= 1 && savedStep <= 9) {
    const step = savedStep;
    if (step >= 6 && !hasOrgWizard && !crSentRestore) return 5;
    return step;
  }
  const cs = project.currentStep;
  if (typeof cs === "number" && !Number.isNaN(cs) && cs >= 1 && cs <= 9) {
    const step = cs;
    if (step >= 6 && !hasOrgWizard && !crSentRestore) return 5;
    return step;
  }
  const prof = project.profilingStatus || "Draft";
  if (prof !== "Finalized") return 1;
  return 2;
}
